// WARNING: Tokens are stored in localStorage without encryption.
// Do NOT store secrets in production without a proper storage strategy.

export type StoredUser = {
  id?: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  roles?: string[];
  permissions?: string[];
};

export type StoredAuth = {
  accessToken: string | null;
  refreshToken: string | null;
  // Epoch milliseconds when the access token expires
  expiresAt: number | null;
  // Epoch milliseconds when this auth snapshot was written.
  savedAt?: number | null;
  remember?: boolean;
  tenantId?: string | null;
  user?: StoredUser | null;
};

import { brand } from "@config/branding";

export const AUTH_KEY = `${brand.storagePrefix}:auth:v1`;
export const AUTH_LOGOUT_BROADCAST_KEY = `${brand.storagePrefix}:auth:logout:v1`;
const KEY = AUTH_KEY;

export type AuthLogoutReason =
  | "session-expired"
  | "idle-timeout"
  | "manual"
  | "unknown";
export type AuthLogoutBroadcast = {
  reason: AuthLogoutReason;
  at: number;
  nonce: string;
};

function normalizeLogoutReason(value: unknown): AuthLogoutReason {
  return value === "session-expired" ||
    value === "idle-timeout" ||
    value === "manual"
    ? value
    : "unknown";
}

function loadFrom(storage: Storage, remember: boolean): StoredAuth | null {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed &&
      "accessToken" in parsed &&
      "refreshToken" in parsed &&
      "expiresAt" in parsed
    ) {
      return {
        accessToken: parsed.accessToken ?? null,
        refreshToken: parsed.refreshToken ?? null,
        expiresAt:
          typeof parsed.expiresAt === "number" ? parsed.expiresAt : null,
        savedAt: typeof parsed.savedAt === "number" ? parsed.savedAt : null,
        remember:
          typeof parsed.remember === "boolean" ? parsed.remember : remember,
        tenantId: "tenantId" in parsed ? parsed.tenantId : null,
        user: parsed.user ?? null,
      } as StoredAuth;
    }
    return null;
  } catch {
    return null;
  }
}

export function loadAuth(): StoredAuth | null {
  if (typeof window === "undefined") return null;
  return (
    loadFrom(window.localStorage, true) ??
    loadFrom(window.sessionStorage, false)
  );
}

function resolveRememberPreference(explicit?: boolean): boolean {
  if (typeof explicit === "boolean") return explicit;
  if (typeof window === "undefined") return false;

  try {
    if (window.localStorage.getItem(KEY)) return true;
    if (window.sessionStorage.getItem(KEY)) return false;
  } catch {
    // fall through to persistent default
  }

  return false;
}

export function saveAuth(data: StoredAuth, opts?: { remember?: boolean }) {
  if (typeof window === "undefined") return;
  const remember = resolveRememberPreference(opts?.remember);
  const payload = JSON.stringify({ ...data, remember, savedAt: Date.now() });

  try {
    if (remember) {
      // Replace the current snapshot atomically. Removing it first broadcasts a
      // temporary logout to other installed windows and can clear their tenant.
      window.sessionStorage.removeItem(KEY);
      window.localStorage.setItem(KEY, payload);
    } else {
      window.localStorage.removeItem(KEY);
      window.sessionStorage.setItem(KEY, payload);
    }
  } catch {
    // ignore storage errors
  }
}

export function clearAuth() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // ignore
  }
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    // ignore
  }
}

export function broadcastAuthLogout(reason: AuthLogoutReason = "manual") {
  if (typeof window === "undefined") return;
  try {
    const payload: AuthLogoutBroadcast = {
      reason,
      at: Date.now(),
      nonce: Math.random().toString(36).slice(2),
    };
    window.localStorage.setItem(
      AUTH_LOGOUT_BROADCAST_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // ignore broadcast failures; local logout still clears the active tab.
  }
}

export function parseAuthLogoutBroadcast(
  value: string | null,
): AuthLogoutBroadcast | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      reason: normalizeLogoutReason(parsed.reason),
      at: typeof parsed.at === "number" ? parsed.at : Date.now(),
      nonce: typeof parsed.nonce === "string" ? parsed.nonce : "",
    };
  } catch {
    return null;
  }
}
